import React, { useEffect, useRef, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import useSWR from 'swr';
import { useNavigate, useParams } from 'react-router-dom';
import {
	Text,
	Container,
	Center,
	Stack,
	Button,
	useToast,
	HStack,
	Heading,
	Box,
	Accordion,
	AccordionItem,
	AccordionButton,
	AccordionPanel,
	AccordionIcon,
	Grid,
	Flex,
} from '@chakra-ui/react';
import baseService from '../../services/base';
import { downloadExemploPerguntas } from '../../services/exemploService';
import { QuestionnaireHeaderNew } from '../../components/Form';
import Loader from '../../components/Feedback/Loader';
import { SuccessModal } from '../../components/Modals';
import {
	CustomRadioGroup,
	CustomTextarea,
	InputText,
	Select,
} from '../../components/Input';
import { fetchData } from '../../services/fetch';
import { templateSchema } from '../../core/validations/survey_template';
import Options from './Options';
import GridOptions from '../../components/Grid/GridOptions';
import GridPreview from '../../components/Grid/GridPreview';
import {
	alternativesTotalOptions,
	formatQuestionObject,
	mudouEstrutura,
	questionTypeOptions,
} from './resources';
import { handleFileChange } from './file-handler';
import { useCatalogoDeClassificacao } from '../../hooks/useCatalogoDeClassificacao';
import QuestionCardCertification from '../../components/Card/Question-card-certification';
import EditQuestionCard, { moveCardQuestions, mapQuestionToForm } from '../../components/Card/EditQuestionCard';
import ClassificacaoMassaModal from '../../components/Modals/ClassificacaoMassaModal';
import { refletirClassificacaoEmMassa } from './refletirClassificacao';

export default function SurveyTemplateForm() {
	const toast = useToast();
	const { id: surveyId } = useParams();
	const navigate = useNavigate();

	const inputFile = useRef(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
	const [isCheckboxSelected, setIsCheckboxSelected] = useState(false);
	const [optionsTotal, setOptionsTotal] = useState(0);
	const [templateObjects, setTemplateObjects] = useState([]);
	const [success, setSuccess] = useState(false);
	const [versionOptions, setVersionOptions] = useState([]);
	const [massaAberta, setMassaAberta] = useState(false);
	const [questoesDoTemplate, setQuestoesDoTemplate] = useState([]);
	const [isGridInitialized, setIsGridInitialized] = useState(false);

	const {
		data: templateData,
		isLoading,
	} = useSWR(surveyId ? `/survey-template/${surveyId}` : null, fetchData);

	const methods = useForm({
		defaultValues: {
			name: '',
			templateCode: '',
			form: [],
		},
		resolver: yupResolver(templateSchema),
		mode: 'onChange',
	});

 const {
		handleSubmit,
		register,
		control,
		getValues,
		setValue,
		reset,
		formState: { errors, isValid, isDirty },
	} = methods;

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'form',
	});

	const templateName = useWatch({
		control,
		name: 'name',
	});

	const templateCode = useWatch({
		control,
		name: 'templateCode',
	});

	const questionType = useWatch({
		control,
		name: `form.${fields?.length - 1}.type`,
	});

 	const certificationPrerequisite = useWatch({
		control,
		name: `form.${fields?.length - 1}.isCertified`,
	});

	const hasHelper =
		useWatch({
			control,
			name: `form.${fields?.length - 1}.hasHelper`,
		}) || 'Não';

	const watchedGridRows = useWatch({
		control,
		name: `form.${fields?.length - 1}.gridRows`,
	});

	const watchedGridColumns = useWatch({
		control,
		name: `form.${fields?.length - 1}.gridColumns`,
	});

 const {
  fields: options,
  append: appendOptions,
  remove: removeOptions,
 } = useFieldArray({
  control,
  name: `form.${fields?.length - 1}.options`,
 });

 const {
  fields: gridRows,
  append: appendGridRow,
  remove: removeGridRow,
 } = useFieldArray({
  control,
  name: `form.${fields?.length - 1}.gridRows`,
 });

 const {
  fields: gridColumns,
  append: appendGridColumn,
  remove: removeGridColumn,
 } = useFieldArray({
  control,
  name: `form.${fields?.length - 1}.gridColumns`,
 });

 useEffect(() => {
		if (!isLoading && templateData?.template) {
      const { name, code } = templateData.template;
			const questions = templateData.questions;
			// O modal de massa precisa do id_question_template de cada questão: é
			// por ele que o lote grava, fora do fluxo de versionamento.
			setQuestoesDoTemplate(
				(questions || []).map((question) => ({
					id_question_template: question.id_question_template,
					code: question.code,
					title: question.questionTitleTemplate?.[0]?.description || '',
				}))
			);
      setVersionOptions(templateData.versions.map((v) => ({label: `v${parseFloat(v.version).toFixed(1)}`, value: v.id_survey_template.toString()})))
      
			reset({
				name,
				templateCode: code,
				form: questions.map((question) => mapQuestionToForm(question)[0]),
			});
	
			addQuestion();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading, templateData]);

	useEffect(() => {
		if (questionType === 'multiple_choice') {
			setIsCheckboxSelected(true);
		} else {
			setIsCheckboxSelected(false);
		}

		// Inicializar grid com valores padrão quando tipo grid é selecionado
		if (questionType === 'grid' && !isGridInitialized && gridRows.length === 0) {
			setIsGridInitialized(true);
			// Adicionar 3 linhas padrão
			appendGridRow({ description: '', row_order: 0 });
			appendGridRow({ description: '', row_order: 1 });
			appendGridRow({ description: '', row_order: 2 });
			
			// Adicionar 4 colunas padrão
			appendGridColumn({ description: '' });
			appendGridColumn({ description: '' });
			appendGridColumn({ description: '' });
			appendGridColumn({ description: '' });
		} else if (questionType !== 'grid') {
			setIsGridInitialized(false);
		}
	}, [questionType]);	useEffect(() => {
		if (
			(questionType === 'text' ||
				questionType === 'percentage' ||
				questionType === 'number' ||
				questionType === 'money' ||
				questionType === 'grid') &&
			options?.length
		) {
			setOptionsTotal(0);
			removeOptions();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [questionType, options?.length]);

    
  const showErrorToast = (message) => {
    toast({
      title: 'Erro',
      description: message,
      status: 'error',
    });
  };

  const catalogoDeClassificacao = useCatalogoDeClassificacao();

 const onChangeFile = (event) => {
    const file = event.target.files[0];
    handleFileChange(file, setTemplateObjects, showErrorToast, catalogoDeClassificacao);
  };

  const addQuestion = () => {
    setOptionsTotal(0);
    append({
      code: '',
      title: '',
      type: '',
      previousYears: 'Não',
      isRequired: 'Não',
      evidenceOnFile: 'Não',
      evidenceInImages: 'Não',
      hasAverage: 'Não',
      hasSum: 'Não',
      hasHelper: 'Não',
      gridRows: [],
      gridColumns: [],
    });
  };

  const handleAddGridRow = (questionIndex) => {
    appendGridRow({
      description: '',
      row_order: gridRows.length,
    });
  };

  const handleRemoveGridRow = (questionIndex, rowIndex) => {
    removeGridRow(rowIndex);
  };

  const handleAddGridColumn = (questionIndex) => {
    appendGridColumn({
      description: '',
    });
  };

  const handleRemoveGridColumn = (questionIndex, colIndex) => {
    removeGridColumn(colIndex);
  };
  
  const onSubmit = (data) => {
    alert(JSON.stringify(data));
  };

  const handleSubmitSuccessful = () => {
    setIsSubmitSuccessful(false);
    navigate('/templates');
  };

	const handlenumberOfAlternativesChange = (e) => {
		const count = parseInt(e.target.value);
		if (count > optionsTotal) {
			for (let i = optionsTotal; i < count; i++) {
				appendOptions(
					{
						description: '',
					},
					{
						shouldFocus: false,
					}
				);
			}
		} else if (count < optionsTotal) {
			for (let i = optionsTotal - 1; i >= count; i--) {
				removeOptions(i);
			}
		}
		setOptionsTotal(count);
	};

	const submitFirst = async () => {
		try {
			setIsSubmitting(true);

			const allQuestions = fields.slice(0, -1).map(formatQuestionObject);
			
			if (templateObjects.length > 0) {
				const formattedTemplateObjects = templateObjects?.map(formatQuestionObject);
				allQuestions.push(...formattedTemplateObjects);
			}
			const form = {
				template: {
					name: templateName ? templateName : templateData.template.name,
					code: templateCode ? templateCode : templateData.template.code,
				},
				Questions: allQuestions,
			};

			await baseService.post(`survey-template/edit-template-with-questions/${surveyId}`, {
				...form,
			});

			setIsSubmitSuccessful(true);

		} catch (err) {
			console.log('err', err);
			showErrorToast('Não foi possível editar o template');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleMoveCard = (direction, index) => {
		const updatedQuestions = moveCardQuestions(fields, direction, index);
    	setValue('form', updatedQuestions, { shouldDirty: true });
  };

	/**
	 * EG-55: reflete na tela a classificação que o lote acabou de gravar.
	 *
	 * A mecânica vive em refletirClassificacao.js, com rede própria. A primeira
	 * versão chamava handleEditQuestion (o caminho individual) dentro de um
	 * forEach — e o reset() da primeira iteração descartava os setValue das
	 * seguintes, então só a PRIMEIRA questão aparecia na tela.
	 */
	const aplicarClassificacaoEmMassaNaTela = (aplicado) =>
		refletirClassificacaoEmMassa(aplicado, {
			getValues,
			setValue,
			append,
			remove,
			reset,
			isDirty,
		});

	const handleEditQuestion = (updated, idx, meta) => {
		// EG-41: trocar só pilar/tema NÃO pode habilitar o Finalizar.
		//
		// A classificação já foi gravada por PATCH quando o modal salvou. Se ela
		// sujasse o formulário, o botão acenderia e induziria o gestor a gerar uma
		// versão nova sem motivo — e versionar soft-deleta a linhagem e recria
		// TODAS as questões com ids novos, exatamente o que faz a propagação
		// perder o alvo.
		const estavaSujo = isDirty;
		const anterior = getValues(`form.${idx}`) || {};
		// O sinal do modal é o confiável; o diff fica como rede para chamadas que
		// não o forneçam.
		const houveMudancaEstrutural =
			meta?.somenteClassificacao === true
				? false
				: mudouEstrutura(updated, anterior);

		Object.keys(updated).forEach((key) => {
			setValue(`form[${idx}][${key}]`, updated[key]);
		});

		// O par append/remove existe para forçar o useFieldArray a reler os
		// valores e re-renderizar os cards — inclusive o rótulo de classificação.
		// O efeito colateral é marcar o formulário como sujo.
		append();
		let questionsSize = getValues().form.length;
		remove(questionsSize - 1);

		if (!houveMudancaEstrutural && !estavaSujo) {
			// Rebaseia o estado limpo sobre os valores atuais: a classificação já
			// está persistida, então ela É o novo ponto de partida. A guarda do
			// estavaSujo preserva uma edição estrutural anterior ainda pendente.
			reset(getValues(), {
				keepValues: true,
				keepErrors: true,
				keepTouched: true,
			});
		}
	}

  const handleVersionSelect = (id) => {
    navigate(`/templates/${id}`, { replace: true });
  }

	if (isLoading) {
		return <Loader />;
	}

	return (
		<>
		<ClassificacaoMassaModal
			isOpen={massaAberta}
			onClose={() => setMassaAberta(false)}
			questoes={questoesDoTemplate}
			onAplicado={aplicarClassificacaoEmMassaNaTela}
		/>
		<form onSubmit={handleSubmit(onSubmit)}>
			<QuestionnaireHeaderNew
				heading='Editar Template'
				submitFunction={submitFirst}
				isSubmitting={isSubmitting}
				isSubmitEnabled={isDirty}
				isFullWidth
				isDisabled={isDirty}
			>
				<Flex align='baseline' justify='flex-start' gap='4'>
          {versionOptions.length > 0 && surveyId && 
            <Select
              defaultValue={surveyId}
              onChange={(e) => handleVersionSelect(e.target.value)}
              options={versionOptions}
              w="80px"
            />
          }
					<Flex w='180px'>
						<InputText
							{...register('templateCode')}
							error={errors.code}
							placeholder='Código do template'
							w='180px'
						/>
					</Flex>
					<InputText
						{...register('name')}
						error={errors.name}
						placeholder='Digite o nome do template'
						w='500px'
					/>					
				</Flex>
			</QuestionnaireHeaderNew>

			<Center my={['8px', '24px']} px={{ xl: '86px' }}>
				<Container maxW={'1700px'} p={0} minH='80vh'>
					<Accordion allowToggle>
						<AccordionItem
							borderRadius='12px 12px 0 0'
							bg='white'
							overflow='hidden'
							pos='relative'
							py='6'
							>
							{({ isExpanded }) => (
								<>
									<AccordionButton
										bg='white'
										h='49px'
										px='6'
										borderRadius='12px 12px 0 0'
										_hover={{
											bg: 'white',
										}}
									>
										<Heading as='h3' fontSize='20px' fontWeight={600}>
											Adicionando perguntas
										</Heading>
										<AccordionIcon ml='auto' />
									</AccordionButton>
									<HStack
										h='49px'
										spacing='24px'
										pos='absolute'
										top='6'
										right='84px'
										opacity={isExpanded ? 1 : '0.4'}
									>
										<Button
											onClick={downloadExemploPerguntas}
											variant='outline'
											bg='none'
											color='green.100'
											w='240px'
											h='full'
											isDisabled={!isExpanded}
										>
											Baixar planilha em lote
										</Button>
										<Button
											onClick={() => {
												inputFile.current.click();
											}}
											variant='primary'
											bg='green.100'
											w='199px'
											h='full'
											isDisabled={!isExpanded}
										>
											Adicionar em lote
										</Button>
									</HStack>
									<AccordionPanel bg='white' p={6} borderRadius='0 0 12px 12px'>
										{fields?.map(
											(field, index) =>
												index === fields?.length - 1 && (
													<Stack spacing='26px' key={field.id}>
														<Grid
															templateColumns={
																isCheckboxSelected ? '171px 5fr 3fr 1fr 145px' : '171px 6fr 3fr 145px'
															}
															gap={6}
														>
															<InputText
																{...register(`form.${index}.code`)}
																label='Código da pergunta'
																placeholder='Código da pergunta'
																labelSize='sm'
																error={errors['form']?.[index]?.code}
															/>

															<InputText
																{...register(`form.${index}.title`)}
																label='Título da pergunta'
																placeholder='Digite o título da pergunta'
																labelSize='sm'
																error={errors['form']?.[index]?.title}
																expandable
															/>

															<Select
																{...register(`form.${index}.type`)}
																placeholder='Selecione o tipo de pergunta'
																label='Tipo de pergunta'
																error={errors['form']?.[index]?.type}
																minW='300px'
																options={questionTypeOptions}
															/>

															{isCheckboxSelected && (
																<CustomRadioGroup
																	register={register}
																	label='Com pontuação?'
																	fieldName={`form.${index}.isCertified`}
																/>
															)}

															<Select
																name={`numberOfAlternatives_${index}`}
																value={optionsTotal.toString()}
																onChange={handlenumberOfAlternativesChange}
																label='Núm. de alternativas'
																placeholder='Selecione'
																isDisabled={
																	questionType === 'text' ||
																	questionType === '' ||
																	questionType === 'percentage' ||
																	questionType === 'number' ||
																	questionType === 'money'
																}
																options={alternativesTotalOptions}
															/>
														</Grid>

														{(questionType === 'multi_choice' || questionType === 'multiple_choice') &&
															optionsTotal !== 0 ? (
															<Options
																index={index}
																register={register}
																errors={errors}
																options={options}
																isCheckboxSelected={isCheckboxSelected}
																certificationPrerequisite={certificationPrerequisite}
															/>
														) : null}

														{questionType === 'grid' && (
															<>
																<GridOptions
																	index={index}
																	register={register}
																	errors={errors}
																	gridRows={gridRows}
																	gridColumns={gridColumns}
																	onAddRow={handleAddGridRow}
																	onRemoveRow={handleRemoveGridRow}
																	onAddColumn={handleAddGridColumn}
																	onRemoveColumn={handleRemoveGridColumn}
																/>
																<Box>
																	<Text fontWeight={600} fontSize='16px' mb='4'>
																		Pré-visualização
																	</Text>
																	<GridPreview gridRows={watchedGridRows || gridRows} gridColumns={watchedGridColumns || gridColumns} />
																</Box>
															</>
														)}

														<HStack spacing={6} justify='flex-start' flexWrap='wrap'>
															<CustomRadioGroup
																register={register}
																label='Pergunta de anos anteriores?'
																fieldName={`form.${index}.previousYears`}
															/>
															<CustomRadioGroup
																register={register}
																label='Pergunta obrigatória?'
																fieldName={`form.${index}.isRequired`}
															/>
															<CustomRadioGroup
																register={register}
																label='Evidências em arquivos?'
																fieldName={`form.${index}.requestFiles`}
															/>

															<CustomRadioGroup
																register={register}
																label='Evidências em fotos/imagens?'
																fieldName={`form.${index}.requestImages`}
															/>
															<CustomRadioGroup
																register={register}
																label='Possui campo de ajuda/helper?'
																fieldName={`form.${index}.hasHelper`}
															/>
															<CustomRadioGroup
																register={register}
																label='Possui SOMA do período?'
																fieldName={`form.${index}.hasSum`}
																hasTooltip
																tooltipLabel='Sinalize com SIM, caso deseje que o input faça uma SOMA de todos os registros do período. Usar somente em casos de inputs alfanuméricos.'
																isDisabled={
																	questionType === 'text' ||
																	questionType === 'multi_choice' ||
																	questionType === 'multiple_choice'
																}
															/>
															<CustomRadioGroup
																register={register}
																label='Possui MÉDIA do período?'
																fieldName={`form.${index}.hasAverage`}
																hasTooltip
																tooltipLabel='Sinalize com SIM, caso deseje que o input faça uma MÉDIA de todos os registros do período. Usar somente em casos de inputs alfanuméricos.'
																isDisabled={
																	questionType === 'text' ||
																	questionType === 'multi_choice' ||
																	questionType === 'multiple_choice'
																}
															/>
														</HStack>

														<Stack spacing={6}>
															<CustomTextarea
																{...register(`form.${index}.helper.description`)}
																label='Texto de ajuda/helper'
																placeholder='Se necessário forneça uma descrição para a pergunta'
																h='111px'
																isDisabled={hasHelper === 'Não'}
															/>
															<InputText
																{...register(`form.${index}.helper.url_video`)}
																label='URL de ajuda/helper'
																placeholder='Informe a URL do video'
																labelSize='sm'
																isDisabled={hasHelper === 'Não'}
															/>
														</Stack>
														<Button
															variant='primary'
															w='212px'
															h='49px'
															ml='auto'
															isDisabled={!isValid}
															onClick={addQuestion}
														>
															Salvar
														</Button>
													</Stack>
												)
										)}
									</AccordionPanel>
								</>
							)}
						</AccordionItem>
					</Accordion>

					{templateData &&
						templateData?.questions?.length <= 0 &&
						fields?.length < 2 &&
						templateObjects?.length < 1 ? (
						<Center h='140px' bg='rgba(173, 175, 179, 0.1)' borderRadius='12px' mt='6'>
							<Text fontWeight={700} color='#5C6066'>
								Nenhuma pergunta foi adicionada
							</Text>
						</Center>
					) : null}


					{fields?.length > 1 || templateObjects?.length > 1 ? (
						<Stack spacing={0} mt='6'>
							{/* EG-41 — CA6.1: a classificação em massa é ação SOBRE a lista de
								perguntas, então mora acima dela. No header do template ela
								disputava espaço com o seletor de versão e ficava truncada,
								além de sugerir que era ação sobre o template como um todo. */}
							<Flex
								justify='space-between'
								align='center'
								bg='white'
								borderRadius='12px'
								px='6'
								py='3'
								mb='4'
							>
								<Text fontSize='sm' color='gray.600'>
									{questoesDoTemplate.length}{' '}
									{questoesDoTemplate.length === 1 ? 'pergunta' : 'perguntas'} neste template
								</Text>
								{surveyId && (
									<Button
										variant='outline'
										size='sm'
										onClick={() => setMassaAberta(true)}
										isDisabled={questoesDoTemplate.length === 0}
									>
										Classificar em massa
									</Button>
								)}
							</Flex>
							<Stack spacing='4'>
								{fields?.sort((a,b) => a.index - b.index).map(
									(field, i) =>
										i !== fields?.length - 1 && (
											<EditQuestionCard 
												key={field.id}
												moveCard={handleMoveCard}
												index={i}
												editQuestion={handleEditQuestion}
												remove={() => remove(i)}
												question={{
													...field,
													description: field.title,
												}}
												QuestionComponent={QuestionCardCertification}
											/>
										)
								)}
							</Stack>
						</Stack>
					) : null}

					<input
						type='file'
						id='file'
						ref={inputFile}
						style={{ display: 'none' }}
						onChange={(e) => onChangeFile(e)}
					/>
				</Container>
			</Center>

			<SuccessModal
				isOpen={isSubmitSuccessful}
				onClose={handleSubmitSuccessful}
				title='Template atualizado com sucesso'
			/>

			<SuccessModal
				isOpen={success}
				onClose={() => setSuccess(false)}
				title='Questão excluída com sucesso'
			/>
		</form>
		</>
	);
}
